import React, { useEffect, useState, useCallback } from "react";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useLocation } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import { formateDate, paginationrowsPerPageOptions } from "../../../utils/commonComponent/commonConstant";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { clienTypes } from "../../../utils/commonComponent/common";
import { AutoCompletes, DateInput } from "../../../utils/Custom/custominput";
import ExcelDownload from "../../../utils/commonComponent/ExcelDownload";
import { SearchToggleButton } from "../../../utils/Custom/Button";

const notNeededColumn = [ "status", "client_id", "servicesNameId", "id", "inspectorId", "jobRegisterId",];

// 1 = Converted, 2 = Pending, 3 = All, 4 = Loss
const STATUS_OPTIONS = [
  { id: 1, label: "Converted" },
  { id: 2, label: "Pending" },
  { id: 4, label: "Loss" },
  { id: 3, label: "All" },
];

const toSqlDate = (val) => {
  if (!val) return null;
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0]; // YYYY-MM-DD
};

const escapeLike = (str) => String(str).replace(/'/g, "''");

const QuotationRatioReport = (props) => {
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const [ratioReportList, setRatioReportList] = useState([]);
  const [quotationOptions, setQuotationOptions] = useState([]);

  const [totalRows, setTotalRows] = useState(0);
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  // New filters
  const [from, setFromDate] = useState(null);
  const [to, setToDate] = useState(null);
  const [selectedQuotationIds, setSelectedQuotationIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState(3); // default: All

  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);

  // Summary counts
  const [summary, setSummary] = useState({
    totalQuotation: 0,
    convertedOrders: 0,
    pendingOrders: 0,
    lossOrders: 0,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getSharedConditions = () => {
    const conditions = [];

    const colMap = {
      client: "qt.clientId",
      poNumber: "poNumber",
      quotationNumber: "qt.quotationNumber",
    };

    const fromSql = toSqlDate(from);
    const toSql = toSqlDate(to);
    if (fromSql) conditions.push(`DATE(qt.date) >= '${fromSql}'`);
    if (toSql) conditions.push(`DATE(qt.date) <= '${toSql}'`);

    if (selectedQuotationIds.length > 0) {
      conditions.push(`qt.id IN (${selectedQuotationIds.map((id) => Number(id)).join(",")})`);
    }

    return conditions;
  };

  // Adds the status condition (Converted / Pending / Loss / All) on top of shared conditions.
  // Converted -> po.id IS NOT NULL
  // Pending   -> po.id IS NULL AND not marked "Not Interested"
  // Loss      -> qt.conversionStatus = 'Not Interested'
  // All       -> no extra condition
  const getStatusCondition = () => {
    if (statusFilter === 1) return "po.id IS NOT NULL";
    if (statusFilter === 2) return "po.id IS NULL AND (qt.conversionStatus IS NULL OR qt.conversionStatus <> 'Not Interested')";
    if (statusFilter === 4) return "qt.conversionStatus = 'Not Interested'";
    return null;
  };

  const buildWhereClause = (conditions) =>
    conditions.length ? `WHERE ${conditions.join(" AND ")}` : "";

  // ---------- Table data query (respects search text + date range + quotation ids + status) ----------
  const getDataQuery = () => {
    const conditions = getSharedConditions();
    const statusCond = getStatusCondition();
    if (statusCond) conditions.push(statusCond);
    const whereClause = buildWhereClause(conditions);

    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${
        rowsPerPage * Number(currentPage)
      }`;
    }

    return `SELECT qt.date, po.quotationId, qt.quotationNumber, po.poFileUrl, po.poNumber, po.poDate, po.amount, po.id, qt.clientId as Client FROM quotation AS qt LEFT JOIN poAcceptance AS po ON po.quotationId = qt.id ${whereClause} ORDER BY qt.id DESC ${paginationSettings};`;
  };

  const getCountQuery = () => {
    const conditions = getSharedConditions();
    const statusCond = getStatusCondition();
    if (statusCond) conditions.push(statusCond);
    const whereClause = buildWhereClause(conditions);

    return `SELECT COUNT(*) AS totalRows FROM quotation AS qt LEFT JOIN poAcceptance AS po ON po.quotationId = qt.id ${whereClause};`;
  };


  const getSummaryQuery = () => {
    const conditions = getSharedConditions();
    const whereClause = buildWhereClause(conditions);

    return `SELECT COUNT(DISTINCT qt.id) AS totalQuotation, COUNT(DISTINCT CASE WHEN po.id IS NOT NULL THEN qt.id END) AS convertedOrders, COUNT(DISTINCT CASE WHEN po.id IS NULL AND (qt.conversionStatus IS NULL OR qt.conversionStatus <> 'Not Interested') THEN qt.id END) AS pendingOrders, COUNT(DISTINCT CASE WHEN qt.conversionStatus = 'Not Interested' THEN qt.id END) AS lossOrders FROM quotation AS qt LEFT JOIN poAcceptance AS po ON po.quotationId = qt.id ${whereClause};`;
  };

  const fetchData = async () => {
    try {
      const [ratioData, countResult, summaryResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
        fetchQuery(getSummaryQuery()),
      ]);

      const modifiedPo = ratioData?.map((item) => ({
        ...item,
        Client: item.Client + (item.clientType ? ` (${clienTypes.find((ct) => ct.value == item.clientType)?.short || ""})` : ""),
    }));

      setRatioReportList(modifiedPo || []);
      setTotalRows(countResult?.[0]?.totalRows || 0);

      const s = summaryResult?.[0] || {};
      setSummary({
        totalQuotation: s.totalQuotation || 0,
        convertedOrders: s.convertedOrders || 0,
        pendingOrders: s.pendingOrders || 0,
        lossOrders: s.lossOrders || 0,
      });
    } catch (error) {
      console.error("Error fetching po data:", error);
    }
  };

  const handleSearch = () => {
    fetchData();
    setSearched(true);
    // Save search state to sessionStorage
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchKey("");
    setFromDate(null);
    setToDate(null);
    setSelectedQuotationIds([]);
    setRatioReportList([]);
    setStatusFilter(3);
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
    setSummary({
      totalQuotation: 0,
      convertedOrders: 0,
      pendingOrders: 0,
      lossOrders: 0,
    });
  };

  const fetchQuotation = async () => {
    const fromSql = toSqlDate(from);
    const toSql = toSqlDate(to);
    let dateCondition = "";
    if (fromSql) dateCondition += ` AND DATE(date) >= '${fromSql}'`;
    if (toSql) dateCondition += ` AND DATE(date) <= '${toSql}'`;

    const data = await fetchQuery(`SELECT id, quotationNumber FROM quotation WHERE status = 1 ${dateCondition} ORDER BY id DESC;`);
    setQuotationOptions(
      data.map((item) => ({
        id: item.id,
        label: item.quotationNumber,
      }))
    );
  };

  useEffect(() => {
    fetchQuotation();
  }, [from, to]);

  useEffect(() => {
    if (searched) fetchData();
  }, [currentPage, rowsPerPage]);

  const handleQuotationChange = useCallback(
    (val) => {
      const values = Array.isArray(val) ? val : [];
      const isSelectAll = values.some((item) => String(item?.id ?? item) === "all");
        // "Select All" chosen -> treat as no restriction
      if (isSelectAll) {
        setSelectedQuotationIds(quotationOptions.map((item) => item.id));
      } else {
        setSelectedQuotationIds(values.map((item) => item?.id ?? item));
      }
      setSearched(false);
    },
    [quotationOptions]
  );

  const handleStatusChange = useCallback((val) => {
    setStatusFilter(Number(val));
    setSearched(false);
  }, []);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 my-3">
        <div className="w-full">
          <DateInput
            id="from-date"
            label="From Date"
            value={from}
            onChange={(val) => {
              setFromDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <DateInput
            id="to-date"
            label="To Date"
            value={to}
            onChange={(val) => {
              setToDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <AutoCompletes
            label="Quotation Number"
            options={[{ id: "all", label: "Select All" }, ...quotationOptions]}
            multiple
            valueKey="id"
            labelKey="label"
            value={selectedQuotationIds}
            onChange={handleQuotationChange}
            isWidth
            limitTags={1}
          />
        </div>
        <div className="w-full">
          <AutoCompletes
            label="Status"
            options={STATUS_OPTIONS}
            valueKey="id"
            labelKey="label"
            value={statusFilter}
            onChange={handleStatusChange}
            isWidth
          />
        </div>
        <div>
          <SearchToggleButton
            searched={searched}
            onSearch={handleSearch}
            onClear={clearSearch}
          />
        </div>
        {ratioReportList?.length > 0 &&
          (permissions.excelDownload || permissions.allAccess) && (
            <div>
              <ExcelDownload
                finalData={ratioReportList.map((row, index) => ({
                  "Sr. No.": currentPage * rowsPerPage + index + 1,
                  Client: row.Client || "-",
                  "Quotation Number":
                    row.quotationId == 0 ? "Verbal" : row.quotationNumber || "",
                  "Quotation Date": formateDate(row.date),
                  "PO Number": row.poNumber || "",
                  "PO Date": formateDate(row.poDate),
                  Amount: row.amount || "",
                  Status: row.poNumber ? "Converted" : row.conversionStatus === "Not Interested" ? "Loss" : "Pending" 
                }))}
                notNeededColumn={[]}
              />
            </div>
          )}
      </div>

      <div className="my-5 font-bold grid grid-cols-2 sm:grid-cols-4 gap-3">
        <p>Total Quotation: {summary.totalQuotation}</p>
        <p>Converted Orders: {summary.convertedOrders}</p>
        <p>Pending Order: {summary.pendingOrders}</p>
        <p>Loss Order: {summary.lossOrders}</p>
      </div>

      <div className="w-full border rounded-md shadow">
        <div className="overflow-y-auto h-auto max-h-[450px]">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="sticky top-0 bg-blue-50 text-xs font-medium text-gray-600 z-10">
              <tr>
                <th className="px-4 py-3 whitespace-nowrap">Sr. No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Client</th>
                <th className="px-4 py-3 whitespace-nowrap">
                  Quotation Number
                </th>
                <th className="px-4 py-3 whitespace-nowrap">Quotation Date</th>
                <th className="px-4 py-3 whitespace-nowrap">PO Number</th>
                <th className="px-4 py-3 whitespace-nowrap">PO Date</th>
                <th className="px-4 py-3 whitespace-nowrap">Amount</th>
                <th className="px-4 py-3 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ratioReportList.length > 0 ? (
                ratioReportList.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="px-4 py-3">
                      {currentPage * rowsPerPage + rowIndex + 1}
                    </td>
                    <td className="px-4 py-3">{row.Client || "-"}</td>
                    <td className="px-4 py-3">
                      {row.quotationId == 0
                        ? "Verbal"
                        : row.quotationNumber || ""}
                    </td>
                    <td className="px-4 py-3">
                      {formateDate(row.date)}
                    </td>
                    <td className="px-4 py-3">{row.poNumber || ""}</td>
                    <td className="px-4 py-3">{formateDate(row.poDate)}</td>
                    <td className="px-4 py-3">{row.amount || ""}</td>
                    <td className="px-4 py-3">{row.poNumber
                      ? "Converted"
                      : row.conversionStatus === "Not Interested"
                        ? "Loss"
                        : "Pending"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 text-sm border-t">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) =>
                handlePaginationChange(0, Number(e.target.value))
              }
              className="border rounded px-2 py-1 text-sm"
            >
              {paginationrowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600">
              {totalRows === 0
                ? "0–0 of 0"
                : `${currentPage * rowsPerPage + 1}–${Math.min(
                    (currentPage + 1) * rowsPerPage,
                    totalRows
                  )} of ${totalRows}`}
            </span>
            <button
              onClick={() => handlePaginationChange(0, rowsPerPage)}
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage - 1, rowsPerPage)
              }
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage + 1, rowsPerPage)
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(
                  Math.ceil(totalRows / rowsPerPage) - 1,
                  rowsPerPage
                )
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationRatioReport;